import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { 
  Activity, Clock, ShieldCheck, Flame, Zap
} from 'lucide-react';
import Achievements from './Achievements';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [punchHistory, setPunchHistory] = useState([]);
  const [punchStatus, setPunchStatus] = useState(null); 
  const [gamification, setGamification] = useState({ streak: 0, badges: [] });
  const [loading, setLoading] = useState(true);
  
  const [timeFilter, setTimeFilter] = useState('weekly'); 
  const [typeFilter, setTypeFilter] = useState('all');    

  const fetchDashboardData = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      
      const fetchSafe = async (url, fallback) => {
        try { 
          const res = await axios.get(url, config); 
          return res.data; 
        } catch (err) { 
          console.error(`Error fetching ${url}:`, err.message); 
          return fallback; 
        }
      };

      // Query limit=200 within schema constraints
      const [s, h, st, g] = await Promise.all([
        fetchSafe(`${API}/admin/dashboard/stats`, null),
        fetchSafe(`${API}/punch-history?limit=200`, []),
        fetchSafe(`${API}/punch-status-today`, null),
        fetchSafe(`${API}/gamification/status`, { streak: 0, badges: [] })
      ]);

      if (s) setStats(s);

      // Defensively unpack array responses
      const historyList = Array.isArray(h) ? h : (h?.records || h?.logs || []);
      setPunchHistory(historyList);

      if (st) setPunchStatus(st);
      if (g) setGamification(g);

    } catch (err) { 
      console.error("Critical error fetching dashboard data", err); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Robust date window calculation without clock skew errors
  const timeFilteredRecords = useMemo(() => {
    const nowTime = new Date().getTime();

    return punchHistory.filter(record => {
      if (!record.timestamp) return false;
      if (timeFilter === 'all-time') return true;

      const recordTime = new Date(record.timestamp).getTime();
      const diffDays = (nowTime - recordTime) / (1000 * 60 * 60 * 24);

      if (timeFilter === 'weekly') {
        return diffDays <= 7 && diffDays >= -1;
      }
      if (timeFilter === 'monthly') {
        return diffDays <= 30 && diffDays >= -1;
      }
      return true;
    });
  }, [punchHistory, timeFilter]);

  const dynamicStats = useMemo(() => ({
    total: timeFilteredRecords.length,
    in: timeFilteredRecords.filter(r => r.punch_type?.toLowerCase() === 'in').length,
    out: timeFilteredRecords.filter(r => r.punch_type?.toLowerCase() === 'out').length
  }), [timeFilteredRecords]);

  const displayRecords = useMemo(() => (
    timeFilteredRecords.filter(record => {
      if (typeFilter === 'all') return true;
      return record.punch_type?.toLowerCase() === typeFilter.toLowerCase();
    })
  ), [timeFilteredRecords, typeFilter]);

  const formatDate = useCallback((isoString) => {
    if (!isoString) return { date: 'N/A', time: 'N/A' };
    try {
      const d = new Date(isoString);
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      };
    } catch {
      return { date: 'N/A', time: 'N/A' };
    }
  }, []);

  const formatTimeOnly = useCallback((isoString) => {
    if (!isoString) return '--:--';
    try {
      return new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '--:--';
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto p-4 sm:p-6 lg:p-8 fade-in relative" data-testid="user-dashboard">
      <div className="mb-8 relative z-10">
        <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-300 tracking-tight" style={{ fontFamily: 'Outfit' }}>
          Hello, {user?.full_name?.split(' ')[0] || 'Employee'}!
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base mt-2 font-medium">Here is your attendance overview.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-8 relative z-10">
        {/* Streak Counter */}
        <div className="relative overflow-hidden bg-gradient-to-br from-orange-500 to-rose-600 rounded-3xl p-8 shadow-xl shadow-orange-500/20 text-white flex-1 flex flex-col justify-between group border border-white/20 transition-transform hover:scale-[1.01] duration-300">
          <div className="relative z-10 flex justify-between items-start mb-6">
            <div>
              <p className="text-orange-100 text-xs font-black uppercase tracking-[0.2em] mb-2 opacity-90">Current Streak</p>
              <div className="flex items-baseline gap-2">
                <span className="text-6xl sm:text-7xl font-black tracking-tighter drop-shadow-md" style={{ fontFamily: 'Outfit' }}>
                  {gamification?.streak || 0}
                </span>
                <span className="text-orange-100/80 font-bold text-lg">Days</span>
              </div>
            </div>
            <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-md border border-white/30 shadow-inner">
              <Flame className="w-8 h-8 text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]" />
            </div>
          </div>

          <div className="relative z-10">
            {(gamification?.streak || 0) >= 5 ? (
              <div className="inline-flex items-center gap-1.5 bg-black/20 text-white text-xs font-bold px-3.5 py-1.5 rounded-full backdrop-blur-md border border-white/10 shadow-sm mt-3">
                <Flame className="w-3.5 h-3.5 text-amber-300 animate-pulse shrink-0" />
                <span>Consistent attendance streak!</span>
              </div>
            ) : (gamification?.streak || 0) > 0 ? (
              <div className="inline-flex items-center gap-1.5 bg-black/20 text-white text-xs font-bold px-3.5 py-1.5 rounded-full backdrop-blur-md border border-white/10 shadow-sm mt-3">
                <Flame className="w-3.5 h-3.5 text-orange-300 shrink-0" />
                <span>Keep going, maintain your streak!</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-1.5 bg-black/20 text-white text-xs font-bold px-3.5 py-1.5 rounded-full backdrop-blur-md border border-white/10 shadow-sm mt-3">
                <Zap className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                <span>Log attendance today to build your streak.</span>
              </div>
            )}
          </div>
        </div>

        {/* Modular Achievements Component */}
        <Achievements gamification={gamification} />
      </div>

      {/* Quick Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 relative z-10">
        <div className="backdrop-blur-xl bg-white/80 dark:bg-[#0B101E]/80 border border-slate-200/80 dark:border-slate-800 p-6 sm:p-8 rounded-3xl shadow-sm hover:-translate-y-0.5 transition-all duration-300">
          <div className="flex items-center justify-between mb-8">
            <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em]">Today's Status</span>
            <div className="p-2 bg-orange-100 dark:bg-orange-500/10 rounded-xl">
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-500 uppercase tracking-wider">Punch In</p>
              </div>
              <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {formatTimeOnly(punchStatus?.punch_in_time)}
              </p>
            </div>
            <div className="h-16 w-px bg-slate-200 dark:bg-slate-700/50"></div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-1.5 mb-2">
                <p className="text-[10px] font-black text-rose-500 uppercase tracking-wider">Punch Out</p>
                <div className="w-2 h-2 rounded-full bg-rose-500"></div>
              </div>
              <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {formatTimeOnly(punchStatus?.punch_out_time)}
              </p>
            </div>
          </div>
        </div>

        <div className="backdrop-blur-xl bg-white/80 dark:bg-[#0B101E]/80 border border-slate-200/80 dark:border-slate-800 p-6 sm:p-8 rounded-3xl shadow-sm hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em]">Total Valid Punches</span>
            <div className="p-2 bg-blue-100 dark:bg-blue-500/10 rounded-xl">
               <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-cyan-400" />
            </div>
          </div>
          <span className="text-5xl sm:text-6xl font-black text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>
            {stats?.lifetime_total || punchHistory.length || 0}
          </span>
        </div>
      </div>

      {/* History Table & Metric Filters */}
      <div className="backdrop-blur-xl bg-white/80 dark:bg-[#0B101E]/80 border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-sm overflow-hidden relative z-10">
        <div className="p-2 border-b border-slate-200/80 dark:border-slate-800">
          <div className="flex bg-slate-100 dark:bg-slate-900/60 p-1.5 rounded-2xl">
            {['weekly', 'monthly', 'all-time'].map((tab) => (
              <button 
                key={tab} 
                onClick={() => { setTimeFilter(tab); setTypeFilter('all'); }} 
                className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all duration-200 capitalize cursor-pointer ${
                  timeFilter === tab 
                    ? 'bg-white dark:bg-slate-800 text-orange-600 dark:text-orange-400 shadow-sm border border-slate-200/60 dark:border-slate-700' 
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
                }`}
              >
                {tab.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-3 divide-x divide-slate-200/80 dark:divide-slate-800 border-b border-slate-200/80 dark:border-slate-800 bg-white/50 dark:bg-slate-900/30">
          {[
            { label: 'Total', val: dynamicStats.total, type: 'all' },
            { label: 'In', val: dynamicStats.in, type: 'in' },
            { label: 'Out', val: dynamicStats.out, type: 'out' }
          ].map((item) => (
            <button 
              key={item.type} 
              onClick={() => setTypeFilter(item.type)} 
              className={`p-6 transition-all duration-200 cursor-pointer ${
                typeFilter === item.type 
                  ? 'bg-orange-50/80 dark:bg-orange-500/10' 
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'
              }`}
            >
              <p className={`text-[10px] font-black uppercase tracking-[0.15em] mb-1 ${
                typeFilter === item.type ? 'text-orange-600 dark:text-orange-400' : 'text-slate-500 dark:text-slate-400'
              }`}>
                {item.label}
              </p>
              <p className={`text-3xl font-extrabold ${
                typeFilter === item.type ? 'text-orange-600 dark:text-orange-400' : 'text-slate-900 dark:text-white'
              }`}>
                {item.val}
              </p>
            </button>
          ))}
        </div>

        <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[420px] overflow-y-auto custom-scrollbar">
          {displayRecords.length === 0 ? (
            <div className="p-12 text-center flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <Activity className="w-8 h-8 text-slate-300 dark:text-slate-600" />
              </div>
              <span className="text-slate-500 dark:text-slate-400 text-sm font-bold">No records found for this period.</span>
            </div>
          ) : (
            displayRecords.map((record, idx) => (
              <div key={record.id || `${record.timestamp}-${idx}`} className="p-5 sm:p-6 flex items-center justify-between hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl shadow-sm border ${
                    record.punch_type?.toLowerCase() === 'in' 
                      ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20' 
                      : 'bg-rose-50 dark:bg-rose-500/10 border-rose-100 dark:border-rose-500/20'
                  }`}>
                    <Activity className={`w-5 h-5 ${
                      record.punch_type?.toLowerCase() === 'in' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                    }`} />
                  </div>
                  <div>
                    <p className="text-base font-bold text-slate-900 dark:text-white mb-0.5">
                      {record.punch_type?.toUpperCase()} <span className="opacity-40 mx-1">•</span> {formatDate(record.timestamp).date}
                    </p>
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {formatDate(record.timestamp).time}
                    </p>
                  </div>
                </div>
                {record.is_valid && (
                  <span className="text-[10px] font-black bg-emerald-100/80 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-500/30 uppercase tracking-[0.1em] shadow-sm">
                    Verified
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;